import React from 'react';
import { fmtMoney, fmtDateShort, fmtDateWithWeekday } from '../helpers';
import { FONT } from '../constants';
import { C } from '../styles';
import type { Row, PayRun, Schedule } from '../types';

interface Props {
  needsHoursRows: Row[];
  includedRows: Row[];
  readyRows: Row[];
  totalHours: number;
  totalGross: number;
  schedule: Schedule | null;
  payRun: PayRun | null;
  onChangePeriod: () => void;
  onAddEmployee: () => void;
}

export default function RunPayrollHeader({
  needsHoursRows, includedRows, readyRows, totalHours, totalGross,
  schedule, payRun, onChangePeriod, onAddEmployee,
}: Props) {
  return (
    <>
      {needsHoursRows.length > 0 && (
        <div style={{ border: '1px solid ' + C.line, borderRadius: 14, padding: '16px 22px', marginBottom: 18, background: '#fff' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
            <span style={{ fontSize: 20 }}>⚠️</span>
            <b style={{ fontSize: 16, color: C.ink, fontWeight: 700 }}>Payroll readiness</b>
            <div style={{ marginLeft: 'auto' }}>
              <span style={{ fontSize: 12, fontWeight: 700, padding: '4px 11px', borderRadius: 20, background: (C as any).amberBg, color: (C as any).amber }}>
                {needsHoursRows.length} needs hours
              </span>
            </div>
          </div>
          {needsHoursRows.slice(0, 3).map((r) => (
            <div key={r.id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, color: (C as any).muted, padding: '5px 0' }}>
              <span>{r.name} has no hours entered for this period.</span>
              <a onClick={() => { const el = document.getElementById('row-' + r.id); if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' }); }} style={{ color: (C as any).brandDark, fontWeight: 700, textDecoration: 'underline', cursor: 'pointer' }}>Add hours</a>
            </div>
          ))}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 18 }}>
        <div style={{ border: '1px solid ' + C.line, borderRadius: 14, padding: '18px 20px', background: '#fff' }}>
          <div style={{ fontSize: 13, color: (C as any).muted, fontWeight: 600, marginBottom: 8 }}>Employees in this run</div>
          <div style={{ fontSize: 26, fontWeight: 800, color: C.ink }}>{includedRows.length} of {readyRows.length}</div>
        </div>
        <div style={{ border: '1px solid ' + C.line, borderRadius: 14, padding: '18px 20px', background: '#fff' }}>
          <div style={{ fontSize: 13, color: (C as any).muted, fontWeight: 600, marginBottom: 8 }}>Total hours</div>
          <div style={{ fontSize: 26, fontWeight: 800, color: C.ink, fontVariantNumeric: 'tabular-nums' }}>{totalHours.toFixed(2)}</div>
        </div>
        <div style={{ border: '1px solid ' + C.line, borderRadius: 14, padding: '18px 20px', background: '#fff' }}>
          <div style={{ fontSize: 13, color: (C as any).muted, fontWeight: 600, marginBottom: 8 }}>Total gross pay</div>
          <div style={{ fontSize: 26, fontWeight: 800, color: C.ink, fontVariantNumeric: 'tabular-nums' }}>{fmtMoney(totalGross)}</div>
        </div>
      </div>

      {schedule && (
        <div style={{ border: '2px solid ' + C.ink, borderRadius: 10, padding: '10px 16px', marginBottom: 14, background: '#fff', display: 'inline-flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 10, height: 10, background: (schedule as any).color || (C as any).brand, borderRadius: '50%' }} />
          <span style={{ fontSize: 13, fontWeight: 600, color: C.ink }}>{schedule.name}</span>
          <span style={{ fontSize: 12, color: (C as any).brandDark }}>· {schedule.periods_per_year} pay periods per year</span>
        </div>
      )}

      {payRun && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 14, marginBottom: 22 }}>
          <div style={{ display: 'flex', gap: 14 }}>
            <div style={{ border: '2px solid ' + C.ink, borderRadius: 10, padding: '10px 16px', background: '#fff', width: 320 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: C.ink, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>Pay period</div>
              <div style={{ border: '1.5px solid ' + C.ink, borderRadius: 6, background: '#F4F6F8', padding: '8px 14px' }}>
                <div style={{ fontSize: 15, fontWeight: 600, color: C.ink }}>{fmtDateShort(payRun.pay_period_start)} to {fmtDateShort(payRun.pay_period_end)}</div>
              </div>
              <div style={{ marginTop: 6, fontSize: 12, display: 'flex', gap: 10, alignItems: 'center' }}>
                <span style={{ color: C.ink, fontWeight: 600 }}>Auto-filled from schedule</span>
                <span style={{ color: C.ink }}>|</span>
                <a onClick={onChangePeriod} style={{ color: (C as any).brandDark, fontWeight: 700, textDecoration: 'underline', cursor: 'pointer' }}>Change period</a>
              </div>
            </div>
            <div style={{ border: '2px solid ' + C.ink, borderRadius: 10, padding: '10px 16px', background: '#fff', width: 320 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: C.ink, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>Next pay date</div>
              <div style={{ border: '1.5px solid ' + C.ink, borderRadius: 6, background: '#F4F6F8', padding: '8px 14px' }}>
                <div style={{ fontSize: 15, fontWeight: 600, color: C.ink }}>{fmtDateWithWeekday(payRun.pay_date)}</div>
              </div>
              <div style={{ marginTop: 6, fontSize: 12, color: C.ink, fontWeight: 600 }}>Auto-filled from schedule</div>
            </div>
          </div>
          <button onClick={onAddEmployee} style={{ background: '#FFFFFF', color: (C as any).brandDark, border: '1.5px solid ' + (C as any).brand, padding: '10.5px 22px', borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 8, fontFamily: FONT }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
              <circle cx="9" cy="7" r="4" />
              <line x1="19" y1="8" x2="19" y2="14" />
              <line x1="22" y1="11" x2="16" y2="11" />
            </svg>
            Add employee
          </button>
        </div>
      )}
    </>
  );
}